import React from "react";
import PropTypes from "prop-types";
import { TableRow, TableCell, TextField, InputAdornment } from "@mui/material";
import DatePicker from 'react-datepicker';
import Icon from '@/components/atoms/Icon/Icon';
import './DataTableFilterRow.scss';

// Extracted style objects

// Clear button as a separate component
const ClearFilterButton = ({ onClick, label }) => (
  <button
    type="button"
    aria-label={`Clear ${label} filter`}
    onClick={onClick}
    className="clear-filter-btn"
    tabIndex={0}
  >
    <span className="datatable-filter-clear-icon">
      <Icon icon="close" style={{ color: '#000000' }} size="small" />
    </span>
  </button>
);

// Filter cell rendering logic
const renderFilterCell = ({
  column,
  dateRanges,
  singleDates,
  openDatePicker,
  filter,
  setOpenDatePicker,
  handleDateRangeChange,
  handleSingleDateChange,
  handleFilterChange,
  CustomInput,
  SingleDateCustomInput,
}) => {
  if (column.filterable === false) return null;

  if (column.filterVariant === 'dateRange') {
    return (
      <DatePicker
        selectsRange
        startDate={dateRanges[column.name_of_db_column]?.startDate || null}
        endDate={dateRanges[column.name_of_db_column]?.endDate || null}
        onChange={dates => handleDateRangeChange(dates, column.name_of_db_column)}
        dateFormat="MM/dd/yyyy"
        placeholderText={column.label_of_column}
        isClearable={dateRanges[column.name_of_db_column]?.startDate && dateRanges[column.name_of_db_column]?.endDate}
        customInput={<CustomInput column={column} className="date-picker-input" />}
        open={openDatePicker === column.name_of_db_column}
        onCalendarOpen={() => setOpenDatePicker(column.name_of_db_column)}
        onCalendarClose={() => setOpenDatePicker('')}
        peekNextMonth
        showMonthDropdown
        useShortMonthInDropdown
        showYearDropdown
        dateFormatCalendar="MMMM"
        yearDropdownItemNumber={15}
        scrollableYearDropdown
        dropdownMode="select"
        minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 30))}
        maxDate={new Date(new Date().getFullYear() + 200)}
      />
    );
  }
  if (column.filterVariant === 'date') {
    return (
      <DatePicker
        selected={singleDates[column.name_of_db_column] || null}
        onChange={date => handleSingleDateChange(date, column.name_of_db_column)}
        dateFormat="MM/dd/yyyy"
        placeholderText={column.label_of_column}
        isClearable={Boolean(singleDates[column.name_of_db_column])}
        customInput={<SingleDateCustomInput column={column} className="date-picker-input" />}
        open={openDatePicker === column.name_of_db_column}
        onCalendarOpen={() => setOpenDatePicker(column.name_of_db_column)}
        onCalendarClose={() => setOpenDatePicker('')}
        peekNextMonth
        showMonthDropdown
        showYearDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={15}
        startDate={new Date(new Date().setFullYear(new Date().getFullYear() - 10))}
        endDate={new Date(new Date().getFullYear() + 10)}
      />
    );
  }
  // Default: text field filter
  return (
    <TextField
      size="small"
      name={column.name_of_db_column}
      value={filter[column.name_of_db_column] || ''}
      onChange={e => handleFilterChange(e)}
      placeholder={column.label_of_column}
      className="text-field-input"
      slotProps={{
        input: {
          endAdornment: !!filter[column.name_of_db_column] && (
            <InputAdornment position="end">
              <ClearFilterButton
                label={column.label_of_column}
                onClick={e => {
                  // Create a synthetic event to clear the filter
                  const event = {
                    ...e,
                    target: {
                      name: column.name_of_db_column,
                      value: ''
                    }
                  };
                  handleFilterChange(event);
                }}
              />
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

const DataTableFilterRow = ({
  columns,
  dateRanges = {},
  singleDates = {},
  openDatePicker = '',
  filter = {},
  setOpenDatePicker = () => { },
  handleDateRangeChange = () => { },
  handleSingleDateChange = () => { },
  handleFilterChange = () => { },
  CustomInput,
  SingleDateCustomInput
}) => (
  <TableRow>
    <TableCell sx={{ width: '60px' }} />
    {columns.map((column, idx) => (
      <TableCell
        className="custom-data-table-cell"
        key={column.name_of_db_column || idx}
        align="left"
      >
        {renderFilterCell({
          column,
          dateRanges,
          singleDates,
          openDatePicker,
          filter,
          setOpenDatePicker,
          handleDateRangeChange,
          handleSingleDateChange,
          handleFilterChange,
          CustomInput,
          SingleDateCustomInput,
        })}
      </TableCell>
    ))}
    <TableCell sx={{ width: '60px' }} />
  </TableRow>
);

DataTableFilterRow.propTypes = {
  columns: PropTypes.array.isRequired,
  dateRanges: PropTypes.object,
  singleDates: PropTypes.object,
  openDatePicker: PropTypes.string,
  filter: PropTypes.object,
  setOpenDatePicker: PropTypes.func,
  handleDateRangeChange: PropTypes.func,
  handleSingleDateChange: PropTypes.func,
  handleFilterChange: PropTypes.func,
  CustomInput: PropTypes.any,
  SingleDateCustomInput: PropTypes.any
};

export default DataTableFilterRow;
